
import { useEffect, useState } from 'react'
import { xxsText } from '../../../../style/text'
import { DocumentPlusIcon, FolderPlusIcon, TrashIcon } from '@heroicons/react/24/outline'
import { CheckIcon, XMarkIcon } from '@heroicons/react/24/solid'
import { motion } from 'framer-motion'
import { uploadFile, createFolder, deleteFile, deleteFolder } from '../../../../api/api'

const ControlPanel = ({ token, utils }: any) => {
    const {
        openedFolder,
        setFullGalleryData,
        selectedItems,
        setSelectedItems,
        setLoading
    } = utils

    const [isAddingFolder, setIsAddingFolder] = useState(false)
    const [newFolderName, setNewFolderName] = useState('')

    useEffect(() => {
        setIsAddingFolder(false)
        setNewFolderName('')
    }, [openedFolder])

    const onAddNewFolder = async () => {
        setLoading(true)
        if (newFolderName) {
            const newFolderData = await createFolder(token, newFolderName)
            setFullGalleryData((prev: any) => {
                const newValue = [...prev, {
                    ...newFolderData,
                    files: []
                }]

                newValue.sort((a, b) => {
                    if (a.name < b.name) {
                        return -1;
                    }
                    if (a.name > b.name) {
                        return 1;
                    }
                    return 0;
                })

                return newValue
            })
            setNewFolderName('')
        }
        setIsAddingFolder(false)
        setLoading(false)
    }

    const onAddNewFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const files = Array.from(e.target.files ?? []);
        if (!files || files.length === 0) return;
        try {
            setLoading(true);

            const folderid = openedFolder.folderid

            const newFiles = await Promise.all(
                files.map(async (file) => {
                    const newFile = await uploadFile(token, file, folderid)
                    return newFile[0]
                })
            )

            setFullGalleryData((prev: any) => {
                const newValue = prev.map((g: any) => {
                    if (g.folderid === openedFolder.folderid) {
                        const files = [...g.files, ...newFiles]
                        files.sort((a, b) => {
                            if (a.name < b.name) {
                                return -1;
                            }
                            if (a.name > b.name) {
                                return 1;
                            }
                            return 0;
                        })

                        return {
                            ...g,
                            files
                        }
                    } else {
                        return g
                    }
                })


                return newValue
            })

        } catch (err) {
            console.error("Picker cancelled", err);
        } finally {
            setLoading(false);
        }
    };

    const onDelete = async () => {
        setLoading(true)
        if (openedFolder) {
            //Delete File
            const fileIdsToDelete = await Promise.all(
                selectedItems.map(async ({ fileid }: any) => {
                    await deleteFile(token, fileid)
                    return fileid
                })
            )

            setFullGalleryData((prev: any) => {
                const newData = prev.map((f: any) => {
                    if (f.folderid === openedFolder.folderid) {
                        const files = f.files.filter((d: any) => !fileIdsToDelete.includes(d.fileid))
                        return {
                            ...f,
                            files
                        }
                    } else {
                        return f
                    }
                })
                return newData
            })

            setSelectedItems([])
        } else {
            //Delete Folder
            const folderIdsToDelete = await Promise.all(
                selectedItems.map(async ({ folderid }: any) => {
                    await deleteFolder(token, folderid)
                    return folderid
                })
            )

            setFullGalleryData((prev: any) => {
                const newData = prev.filter((d: any) => !folderIdsToDelete.includes(d.folderid))
                return newData
            })

            setSelectedItems([])
        }
        setLoading(false)
    }

    return (
        <div className='flex gap-2 rounded-3xl border border-1'>
            <div className='flex gap-2 py-2 pl-2'>
                <motion.div
                    className={
                        isAddingFolder ?
                            `flex gap-1 cursor-pointer text-green-800 border border-green-800 py-1 pl-2 pr-3 rounded-2xl ${xxsText}`
                            :
                            `flex gap-1 cursor-pointer text-green-800 border border-green-800 hover:bg-emerald-50 py-1 pl-2 pr-3 rounded-2xl ${xxsText}`
                    }
                    onClick={() => {
                        if (openedFolder) {
                            const input = document.getElementById('fileUpload')
                            input?.click()
                        } else if (!isAddingFolder) {
                            setIsAddingFolder(true)
                        }
                    }}
                    animate={{
                        width: isAddingFolder ? '220px' : '90px'
                    }}
                    transition={{
                        ease: 'easeInOut'
                    }}
                >
                    {openedFolder ? (
                        <>
                            <DocumentPlusIcon width={20} className='' />
                            <p className='pr-[4px]'>File(s)</p>
                            <input id='fileUpload' type="file" multiple className='hidden' onChange={onAddNewFile} />
                        </>
                    ) : isAddingFolder ?
                        (
                            <>
                                <input
                                    type="text"
                                    placeholder="Folder Name"
                                    className="outline-none w-full"
                                    value={newFolderName}
                                    onChange={e => setNewFolderName(e.target.value)}
                                />
                                <div className='flex gap-1'>
                                    <XMarkIcon
                                        width={25}
                                        className='p-[2px] text-red-500 hover:bg-red-100 transition-all ease-in-out rounded-3xl'
                                        onClick={() => {
                                            setIsAddingFolder(false)
                                            setNewFolderName('')
                                        }}
                                    />
                                    <CheckIcon
                                        width={25}
                                        className='p-[2px] text-green-500 hover:bg-green-100 transition-all ease-in-out rounded-3xl'
                                        onClick={onAddNewFolder}
                                    />
                                </div>
                            </>
                        ) : (
                            <>
                                <FolderPlusIcon width={20} />
                                <p>Folder</p>
                            </>
                        )
                    }
                </motion.div>
                <div
                    className={`flex gap-1 cursor-pointer hover:text-red-600 hover:bg-red-50  py-1 pl-2 pr-3 transition-all ease-in-out rounded-2xl ${xxsText}`}
                    style={{
                        opacity: selectedItems[0] ? 1 : 0,
                        visibility: selectedItems[0] ? 'visible' : 'hidden'
                    }}
                    onClick={onDelete}
                >
                    <TrashIcon width={20} />
                    <p>Delete</p>
                </div>
            </div>
        </div>
    )
}

export default ControlPanel
